import dayjs from "dayjs";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Funnel,
  Trash2,
} from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { LocationReviewSummary } from "../../../models/LocationReviewSummary";
import ConfigurableTableLayout from "../../Common/ConfigurableTableLayout";
import { LanguagesWithTooltip } from "../../Common/LanguagesWithTooltip";
import TableRowActions from "../../Common/TableRowActions";
import ColumnFilterPopover from "../../FacilityListComponents/RefactoredFacilityTable/ColumnFilterPopover";
import StatusItem from "../StatusItem";
import "./styles.scss";

interface ICustomizedReviewsListProps {
  reviewSummariesData: LocationReviewSummary[];
  filterFormData: any;
  totalCount: number;
  onChangeFilterFormData: (filterFormData: any) => void;
  onDeleteReviewSummary: (id: number) => void;
}

type ReviewColumnId =
  | "title"
  | "status"
  | "language"
  | "period"
  | "created_at"
  | "reviews"
  | "actions";

type SortOrder = "asc" | "desc";
type SortableField = "id" | "status" | "created_at" | "start_date" | "end_date";

const ALL_COLUMNS: ReviewColumnId[] = [
  "title",
  "status",
  "language",
  "period",
  "created_at",
  "reviews",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: ReviewColumnId[] = [...ALL_COLUMNS];
const VISIBLE_COLUMNS_STORAGE_KEY =
  "CustomizedReviewsAnalysisPage_RefactoredVisibleColumns";

const COLUMN_SORT_FIELD: Partial<Record<ReviewColumnId, SortableField>> = {
  status: "status",
  period: "start_date",
  created_at: "created_at",
};

const normalizeVisibleColumns = (value: unknown): ReviewColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const filtered = value.filter((col): col is ReviewColumnId =>
    ALL_COLUMNS.includes(col as ReviewColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<ReviewColumnId>(["title", "actions"]);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

const CustomizedReviewsList: React.FC<ICustomizedReviewsListProps> = ({
  reviewSummariesData,
  filterFormData,
  totalCount,
  onChangeFilterFormData,
  onDeleteReviewSummary,
}) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) =>
      _t(`customizedReviewsAnalysisPage.customized_review_ist.${key}`),
    [_t]
  );

  const [visibleColumns, setVisibleColumns] = useState<ReviewColumnId[]>(() => {
    const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!savedColumns) {
      return DEFAULT_VISIBLE_COLUMNS;
    }

    try {
      return normalizeVisibleColumns(JSON.parse(savedColumns));
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );

  const currentSortField = (filterFormData.sort as SortableField) || "id";
  const currentSortOrder: SortOrder =
    filterFormData.order === "asc" ? "asc" : "desc";

  const activeConditionChips = useMemo(() => {
    const chips: { id: string; label: string; onRemove: () => void }[] = [];

    const sortLabelByField: Record<SortableField, string> = {
      id: t("created_at"),
      status: t("Status"),
      created_at: t("created_at"),
      start_date: t("period"),
      end_date: t("period"),
    };

    chips.push({
      id: `sort-${currentSortField}`,
      label: `並び替え順: ${sortLabelByField[currentSortField]} ${
        currentSortOrder === "desc" ? "降順" : "昇順"
      }`,
      onRemove: () => {
        onChangeFilterFormData({
          ...filterFormData,
          sort: "id",
          order: "desc",
          pageIndex: 1,
        });
      },
    });

    if (filterFormData.status) {
      chips.push({
        id: "status-filter",
        label: `Status: ${_t(
          `customizedReviewsAnalysisPage.status.${filterFormData.status}`
        )}`,
        onRemove: () => {
          onChangeFilterFormData({
            ...filterFormData,
            status: null,
            pageIndex: 1,
          });
        },
      });
    }

    return chips;
  }, [
    _t,
    currentSortField,
    currentSortOrder,
    filterFormData,
    onChangeFilterFormData,
    t,
  ]);

  const applySort = (field: SortableField, order: SortOrder) => {
    onChangeFilterFormData({
      ...filterFormData,
      sort: field,
      order,
      pageIndex: 1,
    });
  };

  const onSelectSortField = (columnId: ReviewColumnId) => {
    const field = COLUMN_SORT_FIELD[columnId];
    if (!field) {
      return;
    }

    const nextOrder: SortOrder =
      currentSortField === field && currentSortOrder === "asc" ? "desc" : "asc";

    applySort(field, nextOrder);
  };

  const getColumnLabelText = (columnId: ReviewColumnId) => {
    switch (columnId) {
      case "title":
        return t("title");
      case "status":
        return t("Status");
      case "language":
        return t("language");
      case "period":
        return t("period");
      case "created_at":
        return t("created_at");
      case "reviews":
        return t("reviews");
      case "actions":
        return "操作";
    }
  };

  const getDynamicGridTemplateStyle = () => {
    // Fixed widths so columns never resize as pagination swaps in rows with
    // different content lengths (a `minmax(max-content, ...)` track is
    // recalculated from whatever rows are currently mounted, so it would
    // change size on every page). Content wider than the column truncates
    // with "..." + a title tooltip; the table scrolls horizontally instead
    // of squishing columns. `title` gets a fixed floor plus `1fr` so it
    // absorbs any leftover container width instead of leaving the table
    // looking cut short when there aren't enough columns to fill the row
    // (the floor is a constant, not content-derived, so this still can't
    // resize between pages).
    const widths: Record<ReviewColumnId, string> = {
      title: "minmax(300px, 1fr)",
      status: "140px",
      language: "200px",
      period: "210px",
      created_at: "130px",
      reviews: "80px",
      actions: "56px",
    };

    return {
      gridTemplateColumns: visibleColumns.map((col) => widths[col]).join(" "),
    };
  };

  const toggleColumnVisibility = (columnId: ReviewColumnId) => {
    if (columnId === "title" || columnId === "actions") {
      return;
    }

    setVisibleColumns((prev) => {
      const next = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getFilterOptionsForColumn = (columnId: ReviewColumnId) => {
    if (columnId !== "status") {
      return [];
    }

    const options = [
      "success",
      "not_enough_reviews",
      "gemini_error",
      "gemini_bad_json",
    ];

    return options.map((status) => ({
      label: _t(`customizedReviewsAnalysisPage.status.${status}`),
      value: status,
      checked: filterFormData.status === status,
    }));
  };

  const onApplyHeaderFilter = (columnId: string, value: any[]) => {
    if (columnId === "status") {
      const checkedValues = Array.isArray(value) ? value : [];
      onChangeFilterFormData({
        ...filterFormData,
        status: checkedValues[0] || null,
        pageIndex: 1,
      });
    }
  };

  const onClearHeaderFilter = (columnId: string) => {
    if (columnId === "status") {
      onChangeFilterFormData({
        ...filterFormData,
        status: null,
        pageIndex: 1,
      });
    }
  };

  const createSortButton = (columnId: ReviewColumnId, labelText: string) => {
    const sortField = COLUMN_SORT_FIELD[columnId];
    if (!sortField) {
      return <span className="column-label-text">{labelText}</span>;
    }

    const isActive = currentSortField === sortField;

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(columnId)}
      >
        <span>{labelText}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            currentSortOrder === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const renderFilterTrigger = (columnId: ReviewColumnId) => {
    if (columnId !== "status") {
      return null;
    }

    const options = getFilterOptionsForColumn(columnId);
    const hasActiveFilter = options.some(
      (option: { checked: boolean }) => option.checked
    );

    return (
      <div className="filter-trigger-wrap">
        <button
          type="button"
          className={`filter-trigger-btn ${
            hasActiveFilter ? "has-filter" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            setActiveFilterColumn((prev) =>
              prev === columnId ? null : columnId
            );
          }}
          aria-label={`filter-${columnId}`}
        >
          <Funnel className="funnel-icon" size={14} strokeWidth={2} />
        </button>
        {activeFilterColumn === columnId && (
          <ColumnFilterPopover
            columnId={columnId}
            title={getColumnLabelText(columnId)}
            options={options}
            onApply={(checkedValues) =>
              onApplyHeaderFilter(columnId, checkedValues)
            }
            onClear={() => onClearHeaderFilter(columnId)}
            onClose={() => setActiveFilterColumn(null)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="white-border-panel customized-review-list">
        <ConfigurableTableLayout
          className="customized-review-table-layout"
          onContainerClick={() => setShowColumnSettings(false)}
          controls={
            <div
              className="column-settings-container"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="btn-column-settings"
                onClick={() => setShowColumnSettings((prev) => !prev)}
              >
                表示項目の設定 ⚙️
              </button>
              {showColumnSettings && (
                <div className="column-settings-dropdown">
                  <div className="dropdown-title">表示列の切り替え</div>
                  {ALL_COLUMNS.map((colId) => (
                    <label
                      key={`setting-${colId}`}
                      className={`setting-label ${
                        colId === "actions" ? "is-actions" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(colId)}
                        disabled={colId === "title" || colId === "actions"}
                        onChange={() => toggleColumnVisibility(colId)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="label-text">
                        {getColumnLabelText(colId)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          }
          conditionChips={activeConditionChips}
          onClearAllConditions={() => {
            onChangeFilterFormData({
              ...filterFormData,
              status: null,
              sort: "id",
              order: "desc",
              pageIndex: 1,
            });
          }}
          clearAllLabel="条件をクリア"
          tableContent={
            <div
              className="table-data table-data-grid table-data-grid table-data-grid-pc-only customized-review-table"
              style={getDynamicGridTemplateStyle()}
            >
              <div className="row-th refactored-facility-list-header">
                {visibleColumns.map((columnId) => (
                  <div
                    className={`col-th column-${columnId}`}
                    key={`header-${columnId}`}
                  >
                    <div className="spacing header-cell-inner">
                      {createSortButton(
                        columnId,
                        columnId === "actions"
                          ? ""
                          : getColumnLabelText(columnId)
                      )}
                      {renderFilterTrigger(columnId)}
                    </div>
                  </div>
                ))}
              </div>

              {reviewSummariesData.map(
                (item: LocationReviewSummary, index: number) => (
                  <div className="row-td" key={index}>
                    {visibleColumns.includes("title") && (
                      <div className="col-td title-column">
                        <NavLink
                          to={`/location/${item.location_id}/review-summaries/${item.id}`}
                          className="blue-link"
                          title={`${item.locations?.name || "-"} (${dayjs(
                            item.start_date
                          ).format("YYYY-MM-DD")} - ${dayjs(
                            item.end_date
                          ).format("YYYY-MM-DD")})`}
                        >
                          <span className="cell-ellipsis">
                            {`${item.locations?.name || "-"} (${dayjs(
                              item.start_date
                            ).format("YYYY-MM-DD")} - ${dayjs(
                              item.end_date
                            ).format("YYYY-MM-DD")})`}
                          </span>
                        </NavLink>
                      </div>
                    )}
                    {visibleColumns.includes("status") && (
                      <div className="col-td">
                        <StatusItem status={item.status} />
                      </div>
                    )}
                    {visibleColumns.includes("language") && (
                      <div className="col-td">
                        {item.languages && (
                          <LanguagesWithTooltip
                            languages={item.languages}
                            maxDisplay={3}
                          />
                        )}
                      </div>
                    )}
                    {visibleColumns.includes("period") && (
                      <div className="col-td period">
                        {dayjs(item.start_date).format("YYYY-MM-DD")}
                        {" - "}
                        {dayjs(item.end_date).format("YYYY-MM-DD")}
                      </div>
                    )}
                    {visibleColumns.includes("created_at") && (
                      <div className="col-td">
                        {dayjs(item.created_at).format("YYYY-MM-DD")}
                      </div>
                    )}
                    {visibleColumns.includes("reviews") && (
                      <div className="col-td numeric-column">
                        {item.processed_reviews.length}
                      </div>
                    )}
                    {visibleColumns.includes("actions") && (
                      <div className="col-td">
                        <TableRowActions
                          actions={[
                            {
                              key: "delete",
                              label: t("delete"),
                              icon: Trash2,
                              isDanger: true,
                              onClick: () =>
                                onDeleteReviewSummary(Number(item.id)),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          }
          pagination={{
            filterFormData,
            totalRowsNumber: totalCount,
            onChangeFilterFormData: (nextFilterFormData: any) => {
              onChangeFilterFormData(nextFilterFormData);
            },
          }}
        />
      </div>
    </>
  );
};

export default CustomizedReviewsList;
